import { useCallback } from 'react';
import { getTransferrableProps } from '../utils/getTransferrableProps';
import ReferenceLiteSearchSelect from 'reference-resolvers-lite/components/ReferenceLiteSearchSelect';
import { PIPELINE_STAGE_IN_PIPELINE } from '../resolvers/CustomResolverReferenceTypes';
import { createCachedCustomResolver } from '../resolvers/createCachedCustomResolver';
import { useHttpClient } from '../client/HttpClientContext';
import { useAdditionalPropertyValues } from '../v2/hooks/useAdditionalPropertyValues';
import { useObjectTypeId } from '../v2/hooks/useObjectTypeId';
import { getPipelinePropertyName, getPipelineStagePropertyName, allowsEmptyPipelineStage } from './pipeline/utils/pipelineUtils';
import { referenceToOptionWithReference } from '../utils/referenceLiteSearchSelectUtils';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useGetClosedateForDealObjects } from '../panels/hooks/useGetClosedateForDealObjects';
import { useObjectId } from '../v2/hooks/useObjectId';
import { useGetLoadStageReferences } from './pipeline/hooks/useGetLoadStageReferences';
import { useStageChangeValidation } from './pipeline/hooks/useStageChangeValidation';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PropertyInputPipelineStageV2 = props => {
  const {
    value,
    inputRef,
    clearable,
    onChangeMultiple,
    placeholder,
    inputMode,
    stageChangeValidatorLoading
  } = props;
  const objectTypeId = useObjectTypeId();
  const objectId = useObjectId();
  const httpClient = useHttpClient();
  const {
    data: additionalValues,
    loading: additionalValuesLoading
  } = useAdditionalPropertyValues({
    pipelineId: getPipelinePropertyName(objectTypeId)
  });
  const {
    loading: closedateLoading,
    getClosedateForDealObjects
  } = useGetClosedateForDealObjects({
    objectTypeId
  });
  const pipelineId = additionalValues === null || additionalValues === void 0 ? void 0 : additionalValues.pipelineId;
  const loadStageReferences = useGetLoadStageReferences({
    httpClient,
    objectTypeId,
    pipelineId,
    inputMode,
    objectId
  });

  // parameterize the resolver with `pipelineId` so it only shows stages in the selected pipeline.
  const propertyResolver = createCachedCustomResolver(PIPELINE_STAGE_IN_PIPELINE, objectTypeId, {
    pipelineId: pipelineId === null ? undefined : pipelineId,
    httpClient
  });

  // Use `onSelectedOptionChange` to handle changes instead of `onChange`,
  // because we need access to the selected `Reference` to read its
  // pipelineId (changing stage should also change pipeline).
  //
  // UISelect's `onChange` only passes the option value (stage id), not the
  // option object. `onSelectedOptionChange` passes the option object, however
  // it is called even when the already-selected option is selected again.
  // This is different from `onChange`, which is only called when the selected
  // option changes.
  //
  // The difference is minor but potentially confusing for callers who expect
  // standard `onChange` semantics.
  const handleSelectedOptionChange = useCallback(e => {
    const newStageId = `${e.target.value.value}`;
    if (newStageId === value) {
      // ignore repeat selection of the already-selected option
      // onChange does not be called when the user selects the already-
      // selected option, but `onSelectedOptionChange` does and we need
      // to manually ignore it
      return;
    }
    const reference = e.target.value.reference;
    const newPipelineId = reference && reference.additionalProperties && reference.additionalProperties.pipelineId ? `${reference.additionalProperties.pipelineId}` : null;
    const closedate = getClosedateForDealObjects({
      currentPipelineId: pipelineId,
      nextPipelineId: newPipelineId,
      currentStageId: value,
      nextStageId: newStageId
    });
    const changes = {
      [getPipelineStagePropertyName(objectTypeId)]: newStageId
    };
    if (newPipelineId && newPipelineId !== pipelineId) {
      changes[getPipelinePropertyName(objectTypeId)] = newPipelineId;
    }
    if (closedate !== '') {
      // closedate should never be made empty automatically. We can only set a new date
      changes['closedate'] = closedate;
    }
    onChangeMultiple(changes);
  }, [getClosedateForDealObjects, objectTypeId, onChangeMultiple, pipelineId, value]);
  if (closedateLoading || stageChangeValidatorLoading || additionalValuesLoading) {
    return null;
  }
  return /*#__PURE__*/_jsx(ReferenceLiteSearchSelect, Object.assign({}, getTransferrableProps(props), {
    // We need to set cache as false in order for the governance stage validation to apply
    // The reason for this is that CDP currently has no context as to when a property value gets saved
    // We fetch the validation info based off of the objectId, which tells us what stages are valid based off the record's current state
    // Whenever a record's stage is changed and saved, we need to recalculate the set of enabled/disabled fetches by refetching the validation
    // By default UISelect utilizes a cache to store options, so loadOptions would only get called once on mount.
    // By setting cache to false, we tell UISelect to always call loadOptions when the select is opened/closed which refetches the validation for us
    cache: false,
    loadReferences: loadStageReferences,
    referenceToOption: referenceToOptionWithReference
    // NOTE: using onSelectedOptionChange instead of onChange; see above
    ,
    onChange: undefined,
    onSelectedOptionChange: handleSelectedOptionChange,
    resolver: propertyResolver,
    ref: inputRef,
    anchorType: "button",
    value: value,
    clearable: clearable !== undefined ? clearable : allowsEmptyPipelineStage(objectTypeId),
    placeholder: placeholder || /*#__PURE__*/_jsx(FormattedMessage, {
      message: "customerDataProperties.PropertyInputPipelineStageV2.placeholder"
    })
  }));
};
const PropertyInputPipelineStageV2Wrapper = props => {
  const {
    onChangeMultiple,
    inputMode
  } = props;
  const {
    stageChangeValidatorLoading,
    stageValidationModal,
    onChangeWithStageValidation
  } = useStageChangeValidation({
    onChangeMultiple,
    inputMode
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(PropertyInputPipelineStageV2, Object.assign({}, props, {
      onChangeMultiple: onChangeWithStageValidation,
      stageChangeValidatorLoading: stageChangeValidatorLoading
    })), stageValidationModal]
  });
};
export default PropertyInputPipelineStageV2Wrapper;